import { Grid, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SideNavContext, UserContext } from '../../context';
import { getEnvironment, mainNavLinks } from '../../helpers';
import { INavLink, FeatureFlag } from '../../models';
import { SideNav } from '../sideNav';
import { PrintHeader } from './PrintHeader';
import { Breadcrumbs, Footer, Header } from './';
import { Loader } from '../loader';
import { useFlags } from '../../hooks';
import { GlobalContext } from '../../context/global';

const REDIRECT_TO_LEGACY_DELAY = 1000;

export interface IPage {
  children: any;
  title: string;
  showSideBar?: boolean;
  showSideNav?: boolean;
  showBreadcrumbs?: boolean;
  sideBarLinks?: INavLink[];
  isModal?: boolean;
  hasNoContentPadding?: boolean;
  forceMobileNav?: boolean;
  disableDesktopCollapse?: boolean;
  showHelp?: boolean;
  printOrientation?: 'portrait' | 'landscape';
  requiredFeature?: FeatureFlag;
  legacyUrl?: string;
  showUserMenu?: boolean;
  showNews?: boolean;
  showSettings?: boolean;
  pageHasStickyHeaders?: boolean;
  stickyContainer?: boolean;
  customBreadcrumbPath?: string;
  showFooter?: boolean;
}

/**
 * Controls both the <Footer /> text display and the window title.
 */
const CLIENT_NAME = 'BuilderLinq';
export const SIDENAV_WIDTH_COLLAPSED = 100;
export const SIDENAV_WIDTH_EXPANDED = 280;
const HEADER_HEIGHT = 100;
const FOOTER_HEIGHT = 64;
const WARNING_HEIGHT = 48;

const PrintLandscape = React.lazy(() => import('./PrintLandscape'));
const PrintPortrait = React.lazy(() => import('./PrintPortrait'));

export const Page: FC<IPage> = ({
  children,
  title,
  showSideBar,
  showSideNav = true,
  sideBarLinks,
  showBreadcrumbs = true,
  isModal,
  hasNoContentPadding,
  forceMobileNav = false,
  disableDesktopCollapse = false,
  showHelp = true,
  printOrientation = 'portrait',
  requiredFeature,
  legacyUrl,
  showUserMenu = true,
  showNews = true,
  showSettings = true,
  pageHasStickyHeaders = false,
  stickyContainer = false,
  customBreadcrumbPath,
  showFooter = true
}) => {
  const featureFlags = useFlags();
  const { pathname } = useLocation();
  const isMobileWidth = useMediaQuery(`(max-width: 900px)`);
  const isPrint = useMediaQuery(`print`);
  const isMobile = forceMobileNav ? true : isPrint ? false : isMobileWidth;
  const { showNav, setShowNav } = useContext(SideNavContext);
  const contentRef = useContext(GlobalContext).contentRef;

  const { user } = useContext(UserContext);
  const [isRedirectingToLegacy, setIsRedirectingToLegacy] = useState(false);

  const sideNavWidth = useMemo(() => {
    if (!showSideNav) {
      return 0;
    }
    if (showNav || (disableDesktopCollapse && !isMobile)) {
      // Show Icons and Labels
      return SIDENAV_WIDTH_EXPANDED;
    } else if ((!showNav && !isMobile) || (showNav && isMobile)) {
      // Only show Icons
      return SIDENAV_WIDTH_COLLAPSED;
    } else {
      // Hide completely
      return 0;
    }
  }, [showNav, isMobile, showSideNav, disableDesktopCollapse]);

  const environment = getEnvironment();
  const showWarning = environment !== 'prod';

  const classes = useStyles({
    sideNavWidth,
    showWarning,
    hasNoContentPadding,
    showSideNav,
    isMobile,
    isPrint,
    pageHasStickyHeaders
  });

  // Anytime isNavOpen changes set it in localForage. setItem is async but we don't care
  // to wait since we look at this on load only.
  useEffect(() => {
    localStorage.setItem('stonemartin-nav-open', `${showNav}`);
  }, [showNav]);

  useEffect(() => {
    document.title = `${title} | ${CLIENT_NAME}`;
  }, [pathname, title]);

  useEffect(() => {
    if (isMobile && !isPrint) {
      setShowNav(false);
    } else {
      setShowNav(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  useEffect(
    function checkFeatureFlag() {
      if (!user?.ldFlagsInitialized || !requiredFeature?.length || !legacyUrl) {
        return;
      }
      const pageEnabled = featureFlags[requiredFeature];
      if (!pageEnabled) {
        setIsRedirectingToLegacy(true);
        setTimeout(() => {
          window.location.href = legacyUrl;
        }, REDIRECT_TO_LEGACY_DELAY);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, featureFlags]
  );

  // don't render page at all if it is feature flag protected
  // and the user feature flags have not been loaded
  if (requiredFeature && !user.ldFlagsInitialized) {
    return null;
  }

  if (isRedirectingToLegacy) {
    return <Loader type="fullscreen" position="centered" title="Redirecting to legacy site..." />;
  }

  return (
    <div className={classes.appFrame}>
      <React.Suspense fallback={<></>}>
        {printOrientation === 'landscape' ? <PrintLandscape /> : <PrintPortrait />}
      </React.Suspense>
      {!isModal && (
        <>
          <Header
            height={HEADER_HEIGHT}
            sideNavWidth={sideNavWidth}
            showWarning={showWarning}
            warningHeight={WARNING_HEIGHT}
            setShowNav={setShowNav}
            isMobile={isMobile}
            showNav={showNav}
            showSideNav={showSideNav}
            forceMobileNav={forceMobileNav}
            showHelp={showHelp}
            showUserMenu={showUserMenu}
            showNews={showNews}
            showSettings={showSettings}
          />

          <SideNav
            open={showNav || (disableDesktopCollapse && !isMobile)}
            headerHeight={HEADER_HEIGHT}
            warningHeight={showWarning ? WARNING_HEIGHT : 0}
            setShowNav={setShowNav}
            isMobile={isMobile}
            width={sideNavWidth}
            navItems={sideBarLinks?.length ? sideBarLinks : mainNavLinks}
            showSideNav={showSideNav}
            disableDesktopCollapse={disableDesktopCollapse}
          />
        </>
      )}

      {/* BODY CONTENT */}
      <div
        className={clsx(
          !isModal && classes.contentWrapper,
          !isModal && {
            [classes.contentShift]: showNav
          }
        )}
      >
        <PrintHeader />
        {showSideBar && (
          <div className={classes.content}>
            <div className={clsx(classes.mainContent, isModal ? classes.modalContent : null)}>
              <div className={classes.contentArea}>
                <div className={classes.content}>
                  <Breadcrumbs
                    customPath={customBreadcrumbPath}
                    legacyUrl={legacyUrl}
                    requiredFeature={requiredFeature}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={showSideBar && !isMobile ? 10 : 12}>
                      {children}
                    </Grid>
                  </Grid>
                </div>
              </div>
            </div>
          </div>
        )}
        {!showSideBar && (
          <div
            className={stickyContainer ? classes.stickyContainer : classes.content}
            ref={contentRef}
          >
            {showBreadcrumbs && (
              <Breadcrumbs
                customPath={customBreadcrumbPath}
                legacyUrl={legacyUrl}
                requiredFeature={requiredFeature}
              />
            )}
            {children}
          </div>
        )}
      </div>
      {!isModal && showFooter && <Footer sideNavWidth={sideNavWidth} height={FOOTER_HEIGHT} />}
    </div>
  );
};

const useStyles = makeStyles<
  Theme,
  {
    sideNavWidth: number;
    showWarning: boolean;
    hasNoContentPadding: boolean;
    showSideNav: boolean;
    isMobile: boolean;
    isPrint;
    pageHasStickyHeaders: boolean;
  }
>((theme: Theme) => ({
  appFrame: {
    overflowY: ({ pageHasStickyHeaders }) => (pageHasStickyHeaders ? 'visible' : 'auto'),
    overflowX: ({ pageHasStickyHeaders }) => (pageHasStickyHeaders ? 'visible' : 'hidden'),
    flexDirection: 'column',
    minHeight: '100vh',
    display: 'flex'
  },
  contentWrapper: {
    flexGrow: 2,
    width: `100%`,
    margin: 0,
    display: 'flex',
    '@media print': {
      flexDirection: 'column'
    },
    marginTop: ({ showSideNav, showWarning, isMobile, isPrint }) =>
      (isMobile || !showSideNav) && showWarning
        ? HEADER_HEIGHT + WARNING_HEIGHT
        : isPrint
        ? 0
        : HEADER_HEIGHT,
    padding: ({ hasNoContentPadding }) => (hasNoContentPadding ? 0 : theme.spacing(1)),
    overflow: ({ pageHasStickyHeaders }) => (pageHasStickyHeaders ? 'visible' : 'auto'),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    [theme.breakpoints.up('md')]: {
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.enteringScreen
      }),
      padding: ({ hasNoContentPadding }) => (hasNoContentPadding ? 0 : 0)
    }
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    maxWidth: '100%',
    flexGrow: 1,
    padding: ({ hasNoContentPadding }) => (hasNoContentPadding ? 0 : theme.spacing(1, 1, 1)),
    marginBottom: 0,
    overflow: ({ pageHasStickyHeaders }) => (pageHasStickyHeaders ? 'visible' : 'hidden'),
    [theme.breakpoints.up('md')]: {
      marginLeft: ({ sideNavWidth }) => `${sideNavWidth}px`
    },
    '@media print': {
      marginLeft: '0!important'
    }
  },

  stickyContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    height: `calc(100vh - ${FOOTER_HEIGHT + HEADER_HEIGHT}px)`,
    maxWidth: '100%',
    flexGrow: 1,
    padding: ({ hasNoContentPadding }) => (hasNoContentPadding ? 0 : theme.spacing(1, 1, 1)),
    marginBottom: 0,
    overflowY: 'scroll',

    [theme.breakpoints.up('md')]: {
      marginLeft: ({ sideNavWidth }) => `${sideNavWidth}px`
    },
    '@media print': {
      marginLeft: '0!important'
    }
  },
  contentShift: {
    // Account for side nav, plus left margin that would have been added
    // from contentWrapper class rules

    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  sideBarWrapper: {
    paddingLeft: `0 !important`
  },
  helpFloat: {
    display: ({ isMobile }) => (isMobile ? 'none' : 'flex'),
    position: 'fixed',
    bottom: '1rem',
    right: '1rem'
  }
}));
